import { useEffect, useState } from "react";

/**
 * Registers the service worker and watches for updates.
 *
 * Returns `updateAvailable` (a new version has been downloaded and is waiting)
 * and `applyUpdate()` (tells the waiting worker to activate, then reloads).
 *
 * The flow:
 *  1. Browser fetches `sw.js` on register (and every ~24 h after).
 *  2. If the file changed, the new SW installs but waits — it does NOT call
 *     `skipWaiting()` on its own.
 *  3. This hook detects the waiting worker and sets `updateAvailable = true`.
 *  4. The student taps "Update now" → `applyUpdate()` sends SKIP_WAITING →
 *     the new worker activates → `controllerchange` fires → page reloads.
 *
 * No update ever happens mid-question. The student is always in control.
 */
export function useServiceWorkerUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    let cancelled = false;

    const onControllerChange = () => {
      // A new SW just took over — reload so the app runs the new code.
      window.location.reload();
    };

    const trackWaiting = (reg: ServiceWorkerRegistration) => {
      if (cancelled) return;

      // A worker is already waiting (e.g. from a prior visit).
      if (reg.waiting) {
        setUpdateAvailable(true);
        setRegistration(reg);
        return;
      }

      // Watch for a newly installed worker.
      reg.addEventListener("updatefound", () => {
        const installing = reg.installing;
        if (!installing) return;

        installing.addEventListener("statechange", () => {
          if (installing.state === "installed" && navigator.serviceWorker.controller) {
            // New version installed while there's already a controller →
            // this is an update, not the first install.
            if (!cancelled) {
              setUpdateAvailable(true);
              setRegistration(reg);
            }
          }
        });
      });
    };

    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        if (cancelled) return;
        trackWaiting(reg);

        // Check for updates eagerly on every app open, rather than waiting
        // for the browser's 24 h cycle.
        reg.update().catch(() => {
          /* Offline or server error — no update to find. */
        });
      })
      .catch(() => {
        /* Offline-only enhancement; the app works without it. */
      });

    return () => {
      cancelled = true;
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
    };
  }, []);

  const applyUpdate = () => {
    registration?.waiting?.postMessage({ type: "SKIP_WAITING" });
  };

  return { updateAvailable, applyUpdate };
}
